/**
 * Input Sanitization Utility
 * Prevents XSS, SQL injection, and other security vulnerabilities
 */

import logger from './logger';

/**
 * Sanitize HTML to prevent XSS attacks
 */
export const sanitizeHTML = (input: string): string => {
  if (!input) return '';

  // Note: Forward slash (/) is NOT encoded as it's commonly used in dates (19/01/2026),
  // paths, and other legitimate data. It's not a security risk for XSS attacks.
  // The key XSS vectors are: < > " ' &
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
  };

  const reg = /[&<>"']/gi;
  return input.replace(reg, (match) => map[match]);
};

/**
 * Sanitize string for SQL-like operations
 */
export const sanitizeSQL = (input: string): string => {
  if (!input) return '';

  // Remove or escape dangerous SQL characters
  return input
    .replace(/'/g, "''")
    .replace(/;/g, '')
    .replace(/--/g, '')
    .replace(/\/\*/g, '')
    .replace(/\*\//g, '');
};

/**
 * Sanitize filename
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename) return '';

  // Remove path separators and dangerous characters
  return filename
    .replace(/[/\\?%*:|"<>]/g, '')
    .replace(/\.\./g, '')
    .trim();
};

/**
 * Sanitize URL
 */
export const sanitizeURL = (url: string): string => {
  if (!url) return '';

  try {
    const parsed = new URL(url);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      logger.warn('Blocked potentially dangerous URL protocol', { url, protocol: parsed.protocol });
      return '';
    }

    return parsed.toString();
  } catch (error) {
    logger.warn('Invalid URL format', { url, error });
    return '';
  }
};

/**
 * Sanitize email
 */
export const sanitizeEmail = (email: string): string => {
  if (!email) return '';

  return email
    .trim()
    .toLowerCase()
    .replace(/[^\w\s@.-]/gi, '');
};

/**
 * Sanitize phone number
 */
export const sanitizePhone = (phone: string): string => {
  if (!phone) return '';

  // Keep only numbers, +, (), -, and spaces
  return phone.replace(/[^\d+\-()s]/g, '');
};

/**
 * Sanitize alphanumeric input
 */
export const sanitizeAlphanumeric = (input: string): string => {
  if (!input) return '';

  return input.replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Sanitize numeric input
 */
export const sanitizeNumeric = (input: string): string => {
  if (!input) return '';

  return input.replace(/[^0-9]/g, '');
};

/**
 * Sanitize alphabetic input
 */
export const sanitizeAlphabetic = (input: string): string => {
  if (!input) return '';

  return input.replace(/[^a-zA-Z\s]/g, '');
};

/**
 * Sanitize username
 */
export const sanitizeUsername = (username: string): string => {
  if (!username) return '';

  // Allow letters, numbers, underscores, and hyphens
  return username
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .trim();
};

/**
 * Remove script tags from HTML
 */
export const removeScriptTags = (html: string): string => {
  if (!html) return '';

  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, ''); // Remove event handlers
};

/**
 * Sanitize object (deep sanitization)
 */
export const sanitizeObject = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj === 'string') {
    return sanitizeHTML(obj);
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }

  if (typeof obj === 'object') {
    const sanitized: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        sanitized[key] = sanitizeObject(obj[key]);
      }
    }
    return sanitized;
  }

  return obj;
};

/**
 * Validate and sanitize file
 */
export const sanitizeFile = (file: File, allowedTypes: string[], maxSize: number): { valid: boolean; error?: string } => {
  // Check file size
  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds maximum allowed size of ${maxSize / (1024 * 1024)}MB`,
    };
  }

  // Check file type
  const isAllowedType = allowedTypes.some(type => {
    if (type.endsWith('/*')) {
      return file.type.startsWith(type.replace('/*', ''));
    }
    return file.type === type;
  });

  if (!isAllowedType) {
    return {
      valid: false,
      error: 'File type not allowed',
    };
  }

  // Check filename
  const sanitizedFilename = sanitizeFilename(file.name);
  if (!sanitizedFilename) {
    return {
      valid: false,
      error: 'Invalid filename',
    };
  }

  return { valid: true };
};

/**
 * Truncate string to prevent buffer overflow
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (!str) return '';

  if (str.length <= maxLength) {
    return str;
  }

  return str.substring(0, maxLength);
};

/**
 * Sanitize JSON string
 */
export const sanitizeJSON = (jsonString: string): any => {
  try {
    const parsed = JSON.parse(jsonString);
    return sanitizeObject(parsed);
  } catch (error) {
    logger.error('Invalid JSON string', error);
    return null;
  }
};

/**
 * Escape regex special characters
 */
export const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Remove null bytes (can cause security issues)
 */
export const removeNullBytes = (str: string): string => {
  return str.replace(/\0/g, '');
};

/**
 * Check if a string is a base64 data URL
 */
const isBase64DataUrl = (str: string): boolean => {
  return str.startsWith('data:') && str.includes(';base64,');
};

/**
 * Check if a string is a valid HTTP/HTTPS URL
 */
const isHttpUrl = (str: string): boolean => {
  return str.startsWith('http://') || str.startsWith('https://');
};

/**
 * Sanitize for API request
 * Note: Skips sanitization for base64 data URLs and HTTP URLs to preserve their format
 */
export const sanitizeForAPI = (data: any): any => {
  if (typeof data === 'string') {
    // Skip sanitization for base64 data URLs (images, files, etc.)
    if (isBase64DataUrl(data)) {
      return removeNullBytes(data);
    }
    // Skip sanitization for HTTP/HTTPS URLs (file URLs, image URLs, etc.)
    if (isHttpUrl(data)) {
      return removeNullBytes(data);
    }
    return removeNullBytes(sanitizeHTML(data));
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeForAPI(item));
  }

  if (typeof data === 'object' && data !== null) {
    const sanitized: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        sanitized[key] = sanitizeForAPI(data[key]);
      }
    }
    return sanitized;
  }

  return data;
};

/**
 * Validate MIME type against extension
 */
export const validateMimeType = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  const mimeType = file.type.toLowerCase();

  const mimeMap: Record<string, string[]> = {
    'jpg': ['image/jpeg'],
    'jpeg': ['image/jpeg'],
    'png': ['image/png'],
    'gif': ['image/gif'],
    'pdf': ['application/pdf'],
    'doc': ['application/msword'],
    'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    'xls': ['application/vnd.ms-excel'],
    'xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    'csv': ['text/csv', 'application/vnd.ms-excel'],
  };

  if (!extension || !mimeMap[extension]) {
    return false;
  }

  return mimeMap[extension].includes(mimeType);
};

/**
 * Comprehensive input validation and sanitization
 */
export const validateAndSanitize = (
  input: string,
  type: 'text' | 'email' | 'url' | 'phone' | 'alphanumeric' | 'numeric' | 'html'
): string => {
  if (!input) return '';

  switch (type) {
    case 'email':
      return sanitizeEmail(input);
    case 'url':
      return sanitizeURL(input);
    case 'phone':
      return sanitizePhone(input);
    case 'alphanumeric':
      return sanitizeAlphanumeric(input);
    case 'numeric':
      return sanitizeNumeric(input);
    case 'html':
      return removeScriptTags(input);
    case 'text':
    default:
      return sanitizeHTML(input);
  }
};

const sanitizationService = {
  sanitizeHTML,
  sanitizeSQL,
  sanitizeFilename,
  sanitizeURL,
  sanitizeEmail,
  sanitizePhone,
  sanitizeAlphanumeric,
  sanitizeNumeric,
  sanitizeAlphabetic,
  sanitizeUsername,
  removeScriptTags,
  sanitizeObject,
  sanitizeFile,
  truncateString,
  sanitizeJSON,
  escapeRegex,
  removeNullBytes,
  sanitizeForAPI,
  validateMimeType,
  validateAndSanitize,
};

export default sanitizationService;
